import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Calendar from '../../components/common/Calendar';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import apiClient from '../../utils/api';

const ScheduleContainer = styled.div`
  max-width: 1200px;
  width: 100%;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }
`;

const Title = styled.h2`
  font-size: 1.75rem;
  font-weight: 700;
  color: #000000;
`;

const CalendarSection = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  margin-bottom: 2rem;
  overflow: hidden;
  
  @media (max-width: 768px) {
    padding: 1rem;
    border-radius: 12px;
  }
`;

const ScheduleInfo = styled.div`
  font-size: 0.875rem;
  color: #6c757d;
  margin-top: 1rem;
  line-height: 1.6;
`;

const ScheduleList = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  
  @media (max-width: 768px) {
    padding: 1rem;
    border-radius: 12px;
  }
`;

const ScheduleListTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 700;
  color: #000000;
  margin-bottom: 1.5rem;
`;

const ScheduleItem = styled.div`
  padding: 1.25rem;
  border: 2px solid #dee2e6;
  border-radius: 12px;
  margin-bottom: 1rem;
  transition: all 0.2s ease;
  cursor: pointer;
  
  &:hover {
    border-color: #000000;
    background: #f8f9fa;
  }
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const ScheduleDate = styled.div`
  font-size: 1rem;
  font-weight: 700;
  color: #000000;
  margin-bottom: 0.5rem;
`;

const ScheduleDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const ScheduleDetail = styled.div`
  font-size: 0.875rem;
  color: #6c757d;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  color: #adb5bd;
  font-size: 1rem;
`;

const ModalOverlay = styled.div<{ isOpen: boolean }>`
  display: ${props => props.isOpen ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9999;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  
  @media (max-width: 768px) {
    align-items: flex-end;
    padding: 0;
  }
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 24px;
  padding: 2rem;
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  
  @media (max-width: 768px) {
    border-radius: 24px 24px 0 0;
    max-height: 80vh;
    padding: 1.5rem;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const ModalTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 700;
  color: #000000;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #6c757d;
  padding: 0.25rem;
  
  &:hover {
    color: #000000;
  }
`;

const ModalForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const ModalActions = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 1.5rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const DeleteConfirmBox = styled.div`
  padding: 1rem;
  border: 2px solid #f1aeb5;
  border-radius: 12px;
  background: #fff5f5;
  color: #842029;
  line-height: 1.5;
`;

const DeleteConfirmTitle = styled.div`
  font-size: 1rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
`;

const DeleteConfirmText = styled.div`
  font-size: 0.875rem;
`;

interface Schedule {
  schedule_no: number;
  schedule_date: string;
  schedule_time: string;
  location: string;
  description: string;
}

const AdminSchedule: React.FC = () => {
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth() + 1);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState<Schedule | null>(null);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [formData, setFormData] = useState({
    time: '',
    location: '',
    description: ''
  });
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [loading, setLoading] = useState(true);

  // 운동일정 목록 조회
  const fetchSchedules = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/schedules');
      if (response.data.success) {
        setSchedules(response.data.data);
      }
    } catch (error) {
      console.error('운동일정 조회 실패:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSchedules();
  }, []);

  const scheduledDates = Array.from(new Set(schedules.map(s => s.schedule_date)));

  const closeModal = () => {
    setIsModalOpen(false);
    setIsDeleteConfirmOpen(false);
    setIsDeleting(false);
  };

  const openScheduleModal = (schedule: Schedule) => {
    setSelectedDate(schedule.schedule_date);
    setEditingSchedule(schedule);
    setIsDeleteConfirmOpen(false);
    setIsDeleting(false);
    setFormData({
      time: schedule.schedule_time,
      location: schedule.location,
      description: schedule.description || ''
    });
    setIsModalOpen(true);
  };

  const handleDateClick = (dateStr: string) => {
    setSelectedDate(dateStr);
    const existingSchedule = schedules
      .filter(s => s.schedule_date === dateStr)
      .sort((a, b) => b.schedule_no - a.schedule_no)[0];
    
    if (existingSchedule) {
      // 기존 일정 수정
      openScheduleModal(existingSchedule);
      return;
    } else {
      // 새 일정 추가
      setEditingSchedule(null);
      setFormData({
        time: '18:00',
        location: '',
        description: ''
      });
    }
    
    setIsModalOpen(true);
  };

  const handleAddClick = () => {
    setSelectedDate('');
    setEditingSchedule(null);
    setIsDeleteConfirmOpen(false);
    setIsDeleting(false);
    setFormData({
      time: '18:00',
      location: '',
      description: ''
    });
    setIsModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // 날짜가 없으면 에러 메시지 표시
    if (!selectedDate) {
      window.alert('날짜를 선택해주세요');
      return;
    }
    
    try {
      if (editingSchedule) {
        // 일정 수정
        const response = await apiClient.put(`/schedules/${editingSchedule.schedule_no}`, {
          schedule_date: selectedDate,
          schedule_time: formData.time,
          location: formData.location,
          description: formData.description
        });
        
        if (response.data.success) {
          await fetchSchedules();
        }
      } else {
        // 일정 추가
        const response = await apiClient.post('/schedules', {
          schedule_date: selectedDate,
          schedule_time: formData.time,
          location: formData.location,
          description: formData.description
        });
        
        if (response.data.success) {
          await fetchSchedules();
        }
      }
      
      setIsModalOpen(false);
      setSelectedDate('');
    } catch (error: any) {
      console.error('일정 저장 실패:', error);
      window.alert(error.response?.data?.detail || '일정 저장에 실패했습니다');
    }
  };

  const handleDelete = async () => {
    if (!editingSchedule) return;

    try {
      setIsDeleting(true);
      const response = await apiClient.delete(`/schedules/${editingSchedule.schedule_no}`);
      
      if (response.data.success) {
        closeModal();
        setEditingSchedule(null);
        setSelectedDate('');
        await fetchSchedules();
      }
    } catch (error: any) {
      console.error('일정 삭제 실패:', error);
      window.alert(error.response?.data?.detail || '일정 삭제에 실패했습니다');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleMonthChange = (newYear: number, newMonth: number) => {
    setCurrentYear(newYear);
    setCurrentMonth(newMonth);
    // TODO: 새로운 월의 일정 불러오기
  };

  return (
    <DashboardLayout title="운동 일정 관리">
      <ScheduleContainer>
        <Header>
          <Title>운동 일정</Title>
          <Button variant="primary" onClick={handleAddClick}>
            + 일정 추가
          </Button>
        </Header>
        
        <CalendarSection>
          <Calendar
            year={currentYear}
            month={currentMonth}
            scheduledDates={scheduledDates}
            selectedDate={selectedDate}
            onDateClick={handleDateClick}
            onMonthChange={handleMonthChange}
            allowPastDates={true}
          />
          <ScheduleInfo>
            ✓ 검은색 테두리 날짜는 일정이 있는 날입니다. 클릭하여 수정/삭제할 수 있습니다.
            <br />
            ✓ 빈 날짜를 클릭하면 새 일정을 추가할 수 있습니다.
          </ScheduleInfo>
        </CalendarSection>
        
        <ScheduleList>
          <ScheduleListTitle>이번 달 일정 목록</ScheduleListTitle>
          {loading ? (
            <EmptyState>로딩 중...</EmptyState>
          ) : schedules.length > 0 ? (
            schedules
              .filter(s => s.schedule_date.startsWith(`${currentYear}-${String(currentMonth).padStart(2, '0')}`))
              .sort((a, b) => {
                const dateCompare = a.schedule_date.localeCompare(b.schedule_date);
                if (dateCompare !== 0) return dateCompare;
                return b.schedule_no - a.schedule_no;
              })
              .map(schedule => (
                <ScheduleItem key={schedule.schedule_no} onClick={() => openScheduleModal(schedule)}>
                  <ScheduleDate>📅 {schedule.schedule_date}</ScheduleDate>
                  <ScheduleDetails>
                    <ScheduleDetail>🕐 {schedule.schedule_time}</ScheduleDetail>
                    <ScheduleDetail>📍 {schedule.location}</ScheduleDetail>
                    <ScheduleDetail>📝 {schedule.description}</ScheduleDetail>
                  </ScheduleDetails>
                </ScheduleItem>
              ))
          ) : (
            <EmptyState>이번 달에 등록된 일정이 없습니다.</EmptyState>
          )}
        </ScheduleList>
        
        {/* 일정 추가/수정 모달 */}
        <ModalOverlay 
          isOpen={isModalOpen}
          onClick={closeModal}
        >
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>
                {isDeleteConfirmOpen ? '일정 삭제 확인' : editingSchedule ? '일정 수정' : '일정 추가'}
              </ModalTitle>
              <CloseButton onClick={closeModal}>×</CloseButton>
            </ModalHeader>
            
            {isDeleteConfirmOpen && editingSchedule ? (
              <>
                <DeleteConfirmBox>
                  <DeleteConfirmTitle>이 일정을 삭제할까요?</DeleteConfirmTitle>
                  <DeleteConfirmText>
                    {editingSchedule.schedule_date} {editingSchedule.schedule_time}
                    <br />
                    {editingSchedule.location}
                  </DeleteConfirmText>
                </DeleteConfirmBox>
                <ModalActions>
                  <Button
                    type="button"
                    variant="danger"
                    size="large"
                    fullWidth
                    onClick={handleDelete}
                    disabled={isDeleting}
                  >
                    {isDeleting ? '삭제 중...' : '삭제'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="large"
                    fullWidth
                    onClick={() => setIsDeleteConfirmOpen(false)}
                    disabled={isDeleting}
                  >
                    취소
                  </Button>
                </ModalActions>
              </>
            ) : (
              <ModalForm onSubmit={handleSubmit}>
                <Input
                  label="날짜"
                  type="date"
                  value={selectedDate || ''}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  required
                />
                
                <Input
                  label="시간"
                  type="time"
                  value={formData.time}
                  onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                  required
                />
                
                <Input
                  label="장소"
                  type="text"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  placeholder="예: 수원체육관"
                  required
                />
                
                <Input
                  label="설명"
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="예: 정기 훈련, 연습 경기 등"
                  required
                />
                
                <ModalActions>
                  {editingSchedule && (
                    <Button
                      type="button"
                      variant="danger"
                      size="large"
                      fullWidth
                      onClick={() => setIsDeleteConfirmOpen(true)}
                    >
                      삭제
                    </Button>
                  )}
                  <Button
                    type="submit"
                    variant="primary"
                    size="large"
                    fullWidth
                  >
                    {editingSchedule ? '수정' : '추가'}
                  </Button>
                </ModalActions>
              </ModalForm>
            )}
          </ModalContent>
        </ModalOverlay>
      </ScheduleContainer>
    </DashboardLayout>
  );
};

export default AdminSchedule;
